import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { DatePicker } from "../components/ui/date-picker";
import { toast } from "sonner";
import { 
  UserPlus,
  ArrowLeft,
  Loader2,
  User,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Building2,
  FileText,
  Save
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdvisorNewClient() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    dob: "",
    address: "",
    city: "",
    notes: ""
  });

  useEffect(() => {
    // Check auth via /auth/me
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          
        });
        if (!response.ok) {
          navigate("/advisor/login");
        }
      } catch (err) {
        navigate("/advisor/login");
      }
    };
    checkAuth();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.mobile.trim()) {
      toast.error("Name and mobile number are required");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/advisor/clients`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      if (response.status === 401) {
        navigate("/advisor/login");
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        toast.success("Client added successfully!");
        navigate(`/advisor/clients/${data.client_id}`);
      } else {
        toast.error(data.detail || "Failed to add client");
      }
    } catch (error) {
      console.error("Error adding client:", error);
      toast.error("Failed to add client. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      {/* Header */}
      <header className="bg-[#EDF1F8]/80 backdrop-blur-xl border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/advisor/clients")}
              className="text-[#3A4557] hover:text-[#1B2A4A]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-500 flex items-center justify-center">
              <UserPlus className="h-5 w-5 text-[#0D1A3A]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#1B2A4A]">Add New Client</h1>
              <p className="text-[#3A4557] text-sm">Enter client details</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Card className="bg-white/50 border-[#C9D4E6]">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <Label className="text-[#2E3B57] flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Full Name *
                </Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter client's full name"
                  className="mt-2 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                  required
                  data-testid="client-name-input"
                />
              </div>

              {/* Mobile */}
              <div>
                <Label className="text-[#2E3B57] flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Mobile Number *
                </Label>
                <Input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Enter 10-digit mobile number"
                  className="mt-2 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                  required
                  data-testid="client-mobile-input"
                />
              </div>

              {/* Email */}
              <div>
                <Label className="text-[#2E3B57] flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address (optional)"
                  className="mt-2 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                  data-testid="client-email-input"
                />
              </div>

              {/* DOB */}
              <div>
                <Label className="text-[#2E3B57] flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date of Birth
                </Label>
                <div className="mt-2">
                  <DatePicker
                    date={formData.dob}
                    onDateChange={(date) => setFormData({...formData, dob: date})}
                    placeholder="Select date of birth"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* City */}
                <div>
                  <Label className="text-[#2E3B57] flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    City
                  </Label>
                  <Input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                    className="mt-2 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                    data-testid="client-city-input"
                  />
                </div>

                {/* Address */}
                <div>
                  <Label className="text-[#2E3B57] flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Address
                  </Label>
                  <Input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter address"
                    className="mt-2 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                    data-testid="client-address-input"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-[#2E3B57] flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Notes
                </Label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any additional notes about the client..."
                  className="mt-2 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] min-h-[100px]"
                  data-testid="client-notes-input"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/advisor/clients")}
                  className="flex-1 border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData.name.trim() || !formData.mobile.trim()}
                  className="flex-1 bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold shadow-lg shadow-[#D4A017]/15"
                  data-testid="save-client-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="h-5 w-5 mr-2" />Save Client</>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
